import { useId, useState } from "react";
import type { AgentTask } from "@forgepoint/shared";
import { ChevronDown } from "lucide-react";
import { ThreadTaskRow } from "./ThreadTaskRow";
import type { getThreadTaskGroups } from "./threadTaskGroups";

interface ThreadTaskListProps {
  groups: ReturnType<typeof getThreadTaskGroups>;
  currentUserId?: string;
  onTaskClick: (task: AgentTask) => void;
  selectedTaskId?: string | null;
}

/** Keeps personal work compact, with the full grouped list expanded above the composer. */
export function ThreadTaskList({
  groups,
  currentUserId,
  onTaskClick,
  selectedTaskId,
}: ThreadTaskListProps) {
  const [expanded, setExpanded] = useState(false);
  const contentId = useId();
  const [expandedTaskId, setExpandedTaskId] = useState<string | null>(null);
  const toggleDescription = (task: AgentTask) => {
    const opening = expandedTaskId !== task.id;
    setExpandedTaskId(opening ? task.id : null);
    const actionId = task.actionParameters?.actionId;
    if (
      opening &&
      selectedTaskId !== task.id &&
      (actionId === "PreviewQuote" || actionId === "FinalizeQuote")
    ) {
      onTaskClick(task);
    }
  };
  const previewTasks = groups.assigned.slice(0, 3);
  const collapsedListLabel = `See all ${groups.total} task${groups.total === 1 ? "" : "s"}`;
  const allTasks = [...groups.assigned, ...groups.others, ...groups.upcoming];
  const sections = [
    { label: "Assigned to you", tasks: groups.assigned },
    { label: "Awaiting action from others", tasks: groups.others },
    { label: "Upcoming", tasks: groups.upcoming },
  ];
  if (groups.total === 0) {
    return null;
  }
  return (
    <div
      className="shrink-0 border-t bg-muted/50"
      role="region"
      aria-label="Thread tasks"
    >
      <div className="mx-auto w-full max-w-3xl px-4 py-2">
        <div
          id={contentId}
          className="max-h-[min(40vh,20rem)] overflow-y-auto overscroll-contain"
        >
          {expanded &&
            sections
              .filter((section) => section.tasks.length > 0)
              .map((section) => (
                <section key={section.label} className="mb-3 last:mb-0">
                  <h3 className="flex items-center gap-2 px-2 py-1.5 text-xs font-medium text-muted-foreground">
                    {section.label}
                    <span className="tabular-nums">{section.tasks.length}</span>
                  </h3>
                  {section.tasks.map((task) => {
                    const blockers = task.blockedByTaskIds
                      .map(
                        (id) =>
                          allTasks.find((candidate) => candidate.id === id)
                            ?.subject,
                      )
                      .filter(Boolean);
                    let dependencyLabel: string | undefined;
                    if (blockers.length > 0) {
                      dependencyLabel = `Waiting on ${blockers.join(", ")}`;
                    }
                    return (
                      <ThreadTaskRow
                        key={task.id}
                        task={task}
                        currentUserId={currentUserId}
                        onTaskClick={onTaskClick}
                        selectedTaskId={selectedTaskId}
                        descriptionExpanded={expandedTaskId === task.id}
                        onDescriptionToggle={toggleDescription}
                        dependencyLabel={dependencyLabel}
                      />
                    );
                  })}
                </section>
              ))}
          {!expanded &&
            previewTasks.map((task) => (
              <ThreadTaskRow
                key={task.id}
                task={task}
                currentUserId={currentUserId}
                onTaskClick={onTaskClick}
                selectedTaskId={selectedTaskId}
                descriptionExpanded={expandedTaskId === task.id}
                onDescriptionToggle={toggleDescription}
                compact
              />
            ))}
        </div>
        {groups.total > previewTasks.length && (
          <button
            type="button"
            onClick={() => {
              setExpanded(!expanded);
              setExpandedTaskId(null);
            }}
            aria-expanded={expanded}
            aria-controls={contentId}
            className="flex items-center gap-1 rounded-sm px-2 py-1.5 text-xs text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <ChevronDown
              className={expanded ? "h-3 w-3 rotate-180" : "h-3 w-3"}
              aria-hidden
            />
            {expanded ? "See less" : collapsedListLabel}
          </button>
        )}
      </div>
    </div>
  );
}
